import { useCallback, useEffect } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop, useMutationInterop } from '@confluence/apollo-relay-interop';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { useContextualEmojiUserPreferencesQuery$data } from './__generated__/useContextualEmojiUserPreferencesQuery.graphql';
import type { useContextualEmojiUserPreferencesMutation } from './__generated__/useContextualEmojiUserPreferencesMutation.graphql';

const UserPreferencesFragment = graphql`
	fragment useContextualEmojiUserPreferencesFragment on UserPreferences {
		contextualEmojiOptOut
	}
`;

type UseContextualEmojiUserPreferencesOptions = {
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	contentType?: string;
	isLivePage?: boolean;
};

export const useContextualEmojiUserPreferences = (
	options?: UseContextualEmojiUserPreferencesOptions,
) => {
	const { createAnalyticsEvent, contentType, isLivePage } = options || {};

	const { data, error } = useQueryInterop<useContextualEmojiUserPreferencesQuery$data>(
		graphql`
			query useContextualEmojiUserPreferencesQuery {
				userPreferences {
					contextualEmojiOptOut
				}
			}
		`,
		{
			fetchPolicy: 'cache-and-network',
		},
	);

	const [updateContextualEmojiOptOutServer] =
		useMutationInterop<useContextualEmojiUserPreferencesMutation>(graphql`
			mutation useContextualEmojiUserPreferencesMutation($contextualEmojiOptOut: Boolean!) {
				updateUserPreferences(userPreferences: { contextualEmojiOptOut: $contextualEmojiOptOut }) {
					contextualEmojiOptOut
					__typename
				}
			}
		`);

	const writeFragmentInterop = useWriteFragmentInterop<any>({
		fragmentSpec: UserPreferencesFragment,
	});

	const isContextualEmojiEnabled = !data?.userPreferences?.contextualEmojiOptOut;

	useEffect(() => {
		if (
			data?.userPreferences &&
			createAnalyticsEvent &&
			contentType !== undefined &&
			isLivePage !== undefined
		) {
			const toggledOn = !data.userPreferences.contextualEmojiOptOut;

			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'success',
					actionSubject: 'contextualEmojiUserPreference',
					source: 'useContextualEmojiUserPreferences',
					attributes: {
						cohort: 'ai',
						contentType: contentType,
						isLivePage,
						toggledOn,
					},
				},
			}).fire();
		}
	}, [data, createAnalyticsEvent, contentType, isLivePage]);

	useEffect(() => {
		if (error && createAnalyticsEvent && contentType !== undefined && isLivePage !== undefined) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'errored',
					actionSubject: 'contextualEmojiUserPreference',
					source: 'useContextualEmojiUserPreferences',
					attributes: {
						cohort: 'ai',
						contentType: contentType,
						isLivePage,
						toggledOn: false,
					},
				},
			}).fire();
		}
	}, [error, createAnalyticsEvent, contentType, isLivePage]);

	const updateContextualEmojiOptOut = useCallback(
		async (newContextualEmojiOptOut: boolean) => {
			writeFragmentInterop({
				fragmentId: '$ROOT_QUERY.userPreferences',
				data: {
					contextualEmojiOptOut: newContextualEmojiOptOut,
					__typename: 'UserPreferences',
				},
			});

			try {
				await updateContextualEmojiOptOutServer({
					variables: { contextualEmojiOptOut: newContextualEmojiOptOut },
				});

				if (createAnalyticsEvent && contentType !== undefined && isLivePage !== undefined) {
					const toggledOn = !newContextualEmojiOptOut;

					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'success',
							actionSubject: 'updateContextualEmojiUserPreference',
							source: 'useContextualEmojiUserPreferences',
							attributes: {
								cohort: 'ai',
								contentType: contentType,
								isLivePage,
								toggledOn,
							},
						},
					}).fire();
				}
			} catch {
				writeFragmentInterop({
					fragmentId: '$ROOT_QUERY.userPreferences',
					data: {
						contextualEmojiOptOut: !newContextualEmojiOptOut,
						__typename: 'UserPreferences',
					},
				});

				if (createAnalyticsEvent && contentType !== undefined && isLivePage !== undefined) {
					const toggledOn = !newContextualEmojiOptOut;

					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'errored',
							actionSubject: 'updateContextualEmojiUserPreference',
							source: 'useContextualEmojiUserPreferences',
							attributes: {
								cohort: 'ai',
								contentType: contentType,
								isLivePage,
								toggledOn,
							},
						},
					}).fire();
				}
			}
		},
		[
			updateContextualEmojiOptOutServer,
			writeFragmentInterop,
			createAnalyticsEvent,
			contentType,
			isLivePage,
		],
	);

	return {
		isContextualEmojiEnabled,
		updateContextualEmojiOptOut,
	};
};
